import { watchEffect } from 'vue';
import { computed, reactive } from 'vue';
import { createApiClient } from '@/api/client';
import { setAuthClient, markAuthReady } from './token';

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;
export const useAuth = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = auth0 => {
    if (instance) return instance;
    // The 'instance' is simply a Vue reactive object
    instance = reactive({
        // ...toRefs(auth0),
        loading: true,
        isAuthenticated: auth0.isAuthenticated,
        user: auth0.user,
        role: computed(() => {
            if (instance.isAuthenticated) {
                if (instance.impersonating) {
                    return instance.impersonationTarget;
                }
                // authGuard force-sets isAuthenticated on public pages (CreateUser, View) where there is no user
                return instance.user?.['https://memoryshare.com/roles'] ?? '';
            }
            return '';
        }),
        funeralHomeId: computed(() => {
            if (instance.user) {
                return instance.user['https://memoryshare.com/funeral_home_id'];
            }
            return 0;
        }),
        oldFuneralHomeId: computed(() => {
            if (instance.user) {
                return instance.user['https://memoryshare.com/oldFuneralHomeId'];
            }
            return null;
        }),
        auth0Client: auth0._client,
        impersonating: computed(() => {
            if (instance.user) {
                return instance.user['https://memoryshare.com/impersonate'];
            }
            return false;
        }),
        // Who a SuperAdmin is impersonating: 'Owner' or 'FuneralHome'.
        // The token has no explicit target claim, so an owner_id with no funeral home id means an owner.
        impersonationTarget: computed(() => {
            const user = instance.user;
            if (user?.['https://memoryshare.com/owner_id'] && !user['https://memoryshare.com/funeral_home_id']) {
                return 'Owner';
            }
            return 'FuneralHome';
        }),
        ownerId: computed(() => instance.user?.['https://memoryshare.com/owner_id'] ?? null),
        popupOpen: false,
        error: null,
        session: 'hello',
        api: createApiClient(),
        // Methods
        /** Authenticates the user using a popup window */
        loginWithPopup: async o => {
            instance.popupOpen = true;

            try {
                await instance.auth0Client.loginWithPopup(o);
            } catch (e) {
                console.error(e);
            } finally {
                instance.popupOpen = false;
            }
            // instance.user = await instance.auth0Client.getUser();
            // instance.isAuthenticated = true;
        },
        /** Handles the callback when logging in using a redirect */
        handleRedirectCallback: async () => {
            instance.loading = true;
            try {
                await instance.auth0Client.handleRedirectCallback();
                // instance.user = await instance.auth0Client.getUser();
                // instance.isAuthenticated = true;
                setAuthenticatedState();
            } catch (e) {
                instance.error = e;
            } finally {
                instance.loading = false;
            }
        },
        /** Authenticates the user using the redirect method */
        loginWithRedirect(o) {
            return instance.auth0Client.loginWithRedirect(o);
        },
        /** Returns all the claims present in the ID token */
        getIdTokenClaims(o) {
            return instance.auth0Client?.getIdTokenClaims(o);
        },
        /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
        getTokenSilently(o) {
            return instance.auth0Client.getTokenSilently(o);
        },
        /** Gets the access token using a popup window */

        getTokenWithPopup(o) {
            return instance.auth0Client.getTokenWithPopup(o);
        },
        /** Logs the user out and removes their session on the authorization server */
        logout(o) {
            return instance.auth0Client.logout(o);
        },
    });
    setAuthClient(instance.auth0Client);
    const setAuthenticatedState = async function () {
        // console.log('setting auth state (session, roles, token, etc)');
        // Initialize our internal authentication state
        if (instance.isAuthenticated) {
            // Get Session Information

            instance.api
                .get(`/users/get-session`)
                .then(response => {
                    instance.session = response.data;
                })
                .catch(() => {
                    instance.session = null;
                })
                .finally(() => {
                    instance.loading = false;
                });
        } else {
            instance.loading = false;
        }
    };

    watchEffect(async () => {
        if (auth0.isAuthenticated.value && !auth0.isLoading.value) {
            // Auth0's initial check is done, so getTokenSilently() can be trusted now.
            // Mark ready before the request below fires, or it would deadlock waiting on itself.
            markAuthReady();
            try {
                // TODO: Check if the `await` here can be removed to speed up app loading.
                // It's only purpose now is to fetch session data and resolve internal loading state.
                await setAuthenticatedState();
            } catch (error) {
                console.error('Failed to initialize auth state:', error);
            }
        } else {
            if (instance.loading && !auth0.isLoading.value) {
                instance.loading = false;
            }
            if (!auth0.isLoading.value) {
                markAuthReady();
            }
        }
    });

    return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export default {
    install(app) {
        // this plugin is a wrapper for the native auth0 vue plugin
        // it's primary purpose is to ensure our legacy usage doesn't break
        // as the native plugin binds to this.$auth0 while we use this.$auth
        // and have custom properties and methods
        let authInstance = useAuth0(app.config.globalProperties.$auth0);
        app.config.globalProperties.$auth = authInstance;
        app.config.globalProperties.$api = authInstance.api;
    },
};
